import { motion } from 'framer-motion';
import {
  Bot,
  MessageCircle,
  Globe,
  Smartphone,
  Clock,
  Languages,
  UserPlus,
  ShieldCheck,
  BarChart3,
  Headphones,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function AIChatbotPage() {
  const channels = [
    {
      icon: MessageCircle,
      title: 'WhatsApp Enquiry Bot',
      description:
        'Respond instantly to admission queries, fee details, and campus information on WhatsApp — the channel parents already trust.',
      color: 'bg-emerald-500',
    },
    {
      icon: Globe,
      title: 'Website Live Chat',
      description:
        'Embed an intelligent chat assistant on your school or college website to guide visitors, capture leads, and schedule campus visits.',
      color: 'bg-blue-500',
    },
    {
      icon: Smartphone,
      title: 'Parent & Student App Chat',
      description:
        'Answer frequent questions inside your mobile app — attendance, homework, holidays, transport routes, and more.',
      color: 'bg-indigo-500',
    },
    {
      icon: Headphones,
      title: 'Voice & Call Assist (Optional)',
      description:
        'Extend support beyond text with smart call routing and guided voice responses for high-volume enquiry seasons.',
      color: 'bg-orange-500',
    },
  ];

  const features = [
    {
      icon: Clock,
      title: '24×7 Instant Responses',
      description:
        'Never miss an enquiry after office hours. Your AI assistant replies in seconds — day or night, weekends included.',
      color: 'bg-cyan-600',
    },
    {
      icon: UserPlus,
      title: 'Smart Lead Capture',
      description:
        'Collect parent name, phone, class of interest, and preferred visit time automatically, then push leads to your admission team.',
      color: 'bg-purple-500',
    },
    {
      icon: Languages,
      title: 'Multi-Language Support',
      description:
        'Serve families in English, Hindi, and regional languages so every parent feels understood and welcome.',
      color: 'bg-pink-500',
    },
    {
      icon: Sparkles,
      title: 'School-Trained Knowledge Base',
      description:
        'Train the bot on your fees, policies, syllabus, events, and FAQs so answers stay accurate and on-brand.',
      color: 'bg-yellow-500',
    },
    {
      icon: ShieldCheck,
      title: 'Human Handoff When Needed',
      description:
        'Escalate complex queries to your counsellor or front desk with full chat history — no parent left waiting.',
      color: 'bg-red-500',
    },
    {
      icon: BarChart3,
      title: 'Enquiry Analytics Dashboard',
      description:
        'Track top questions, conversion rates, peak hours, and response quality to refine admissions outreach.',
      color: 'bg-green-500',
    },
  ];

  const useCases = [
    {
      title: 'Admissions & Campus Visits',
      points: [
        'Share eligibility, fee structure, and seat availability',
        'Book counselling slots and campus tours automatically',
        'Send brochure links and application reminders',
      ],
    },
    {
      title: 'Parent Support Desk',
      points: [
        'Answer fee dues, receipt, and payment queries',
        'Share holiday lists, exam schedules, and circulars',
        'Guide parents to the right department or app section',
      ],
    },
    {
      title: 'Student Helpdesk',
      points: [
        'Guide students on homework, results, and attendance',
        'Share transport routes and library timings',
        'Reduce repetitive calls to the front office',
      ],
    },
  ];

  const deliverables = [
    'WhatsApp Business chatbot configured for enquiry and admissions',
    'Website chat widget branded for your institution',
    'Custom knowledge base built from your FAQs, fees, and policies',
    'Lead capture forms with CRM / WhatsApp notification to your team',
    'Analytics dashboard with enquiry trends and conversion insights',
    'Optional human handoff workflow for counsellor follow-up',
  ];

  const steps = [
    {
      step: '01',
      title: 'Discover & Design',
      description:
        'We map your common enquiries, tone of voice, and preferred channels with your admissions team.',
    },
    {
      step: '02',
      title: 'Train & Integrate',
      description:
        'We train the assistant on your content and connect WhatsApp, website, or app chat to your workflows.',
    },
    {
      step: '03',
      title: 'Launch & Optimize',
      description:
        'Go live with monitoring, monthly refinements, and clear reports on leads and parent satisfaction.',
    },
  ];

  return (
    <div data-testid="ai-chatbot-page" className="pt-20">
      <section className="relative overflow-hidden py-24 bg-gradient-to-br from-navy to-slate-800 text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(16,185,129,0.18),_transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 mb-6">
              <Bot className="h-4 w-4 text-emerald-light" />
              <span className="text-sm font-medium text-slate-100">
                AI Chatbot Solution
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black mb-6 font-heading">
              AI Chatbot for Schools &amp; Colleges
            </h1>
            <p className="text-base sm:text-lg leading-relaxed text-slate-300 max-w-3xl mx-auto mb-8">
              Engage parents and students instantly on WhatsApp, your website, and
              mobile apps. Capture admission enquiries, answer routine questions,
              and free your staff to focus on counselling that matters.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button
                  data-testid="ai-chatbot-demo-button"
                  size="lg"
                  className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
                >
                  Book Free Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="ai-chatbot-contact-button"
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white px-8"
                >
                  Talk to Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section
        data-testid="ai-chatbot-channels-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Meet Parents Where They Already Are
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Deploy intelligent assistants across the channels families use every
              day — starting with WhatsApp for enquiries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {channels.map((channel, index) => (
              <motion.div
                key={channel.title}
                data-testid={`ai-chatbot-channel-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div
                  className={`${channel.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <channel.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {channel.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {channel.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="ai-chatbot-features-section"
        className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Built for Educational Institutions
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Professional AI assistance designed for admissions desks, front
              offices, and parent communication teams.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                data-testid={`ai-chatbot-feature-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div
                  className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6`}
                >
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-navy mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="ai-chatbot-use-cases-section"
        className="py-24 px-6 md:px-12 lg:px-24"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              Practical Use Cases
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              From first enquiry to everyday parent support — one assistant that
              works around the clock.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                data-testid={`ai-chatbot-use-case-${index}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
              >
                <h3 className="text-xl font-semibold text-navy mb-5 font-heading">
                  {useCase.title}
                </h3>
                <ul className="space-y-3">
                  {useCase.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600 leading-relaxed">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-4 font-heading">
              How We Deliver
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A clear, professional rollout — from conversation design to live
              optimisation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative rounded-2xl border border-slate-200 bg-white p-8 shadow-sm"
              >
                <span className="text-4xl font-black text-emerald/20 font-heading">
                  {item.step}
                </span>
                <h3 className="mt-3 text-xl font-semibold text-navy font-heading">
                  {item.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.description}
                </p>
                {index < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-emerald/40" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-12 shadow-sm">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
              What You Receive
            </h2>
            <div className="space-y-4">
              {deliverables.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald mt-0.5 flex-shrink-0" />
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="ai-chatbot-cta-section" className="py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-navy mb-6 font-heading">
            Ready to Automate Enquiries with AI?
          </h2>
          <p className="text-base text-slate-600 mb-8">
            See how VibhumEduSys AI Chatbot can handle WhatsApp enquiries, website
            chats, and parent FAQs for your institution — professionally and at
            scale.
          </p>
          <Link to="/register">
            <Button
              data-testid="ai-chatbot-cta-button"
              size="lg"
              className="bg-emerald hover:bg-emerald-light text-white font-semibold px-8"
            >
              Book Free Demo
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
